import { PreferredLanguage, InsuranceProvider } from '~/types'
import { useMutation } from '~/utils/useMutation'
import { useAuth0 } from '@auth0/auth0-react'
import { useQuery } from 'react-query'
import { useHistory } from 'react-router-dom'

import { storedToken, useSetToken } from '~/utils'
import { BenefitAdministrationType } from '~/utils/constants'
import api from '~/api'
import { format, parse } from 'date-fns'
import { Logger } from '~/utils/logging'

import { secureCache } from '~/utils/secureCache'
import { clearSignupDraft } from '~/utils/signupDraft'
import { useOnboardingNavigation } from '~/utils/useOnboardingNavigation'
import { useUserAuthenticated } from '~/utils/useUserAuthenticated'

const logger = Logger.getInstance()

export enum SignupType {
  HEALTH_PLAN_PROSPECT = 'health_plan_prospect',
  HEALTH_PLAN_MEMBER = 'health_plan_member',
  CARE_ONLY_MEMBER = 'care_only_member',
}

export interface KeyValueEntry<L = string, V = string> {
  label: L
  value: V
}

export interface PreferredLanguageEntry extends KeyValueEntry<string, string> {
  name: string
}

interface NewPatientNameDate {
  firstName: string
  lastName: string
  preferredName: string
  consentForms: number[]
  termsAndConditions: boolean
  dob?: string
}

interface NewPatientData extends NewPatientNameDate {
  phoneNumber?: string
  sex: any | null
  gender: any | null
  pronouns: any | null
  dob: string
}

export interface NewPatientFormState extends NewPatientData {
  insurancePayerId: number | null
  state: any
  preferredLanguage?: PreferredLanguageEntry | null
  preferredLanguageOther?: string
}

const PRONOUNS_KEY = ['pronouns']
const GENDER_IDENTITIES_KEY = ['gender_identities']
const STATES_QUERY_KEY = ['states']
const PREFERRED_LANGUAGES_QUERY_KEY = ['preferredLanguages']
const PAYERS_QUERY_KEY = ['payers']

export const usePronouns = () =>
  useQuery(
    PRONOUNS_KEY,
    async () => (await api.rest.get<any[]>(`/facts/pronouns/?is_deleted=false`)).data,
    {
      staleTime: Infinity,
      placeholderData: [],
      select: data =>
        data?.map(s =>
          Object.assign(s, {
            label: s.label,
            value: s.label,
          }),
        ),
    },
  )

export const useGenderIdentities = () =>
  useQuery(
    GENDER_IDENTITIES_KEY,
    async () => (await api.rest.get<any[]>(`/facts/gender-identities/?is_deleted=false`)).data,
    {
      staleTime: Infinity,
      placeholderData: [],
      select: data =>
        data?.map(s =>
          Object.assign(s, {
            label: s.label,
            value: s.label,
          }),
        ),
    },
  )

export interface StateItemsProps {
  name: string
  abbreviation: string
  canServiceBenefit: boolean
  canService: boolean
  label: string
  value: string
}
export const useStates = () =>
  useQuery<StateItemsProps[]>(
    STATES_QUERY_KEY,
    async () => (await api.rest.get<any[]>('/states/')).data,
    {
      staleTime: Infinity,
      placeholderData: [],
      select: data =>
        data?.map(s =>
          Object.assign(s, {
            label: s.name,
            value: s.abbreviation,
            canService: s.canService,
            canServiceBenefit: s.canServiceBenefit,
          }),
        ),
    },
  )

export const usePreferredLanguages = () =>
  useQuery<PreferredLanguage[]>(
    PREFERRED_LANGUAGES_QUERY_KEY,
    async () =>
      (await api.rest.get<PreferredLanguage[]>(`/facts/preferred-languages/`, { is_hidden: false }))
        .data,
    {
      staleTime: Infinity,
      placeholderData: [],
    },
  )
export const usePayers = (benefitAdministration?: BenefitAdministrationType) => {
  const params: { fireflyAccepted: boolean; benefitAdministration?: BenefitAdministrationType } = {
    fireflyAccepted: true,
  }
  if (benefitAdministration) {
    params.benefitAdministration = benefitAdministration
  }

  return useQuery(
    [...PAYERS_QUERY_KEY, benefitAdministration],
    async () => (await api.rest.get<InsuranceProvider[]>('/insurance/provider/', params)).data,
    {
      staleTime: Infinity,
      placeholderData: [],
      onSuccess: data => {
        for (const payer of data) {
          // This serves the same purpose as Image.prefetch for the web to preload these images
          const img = new Image()
          if (payer.logo) img.src = payer.logo
        }
      },
    },
  )
}

export const GENDERS_DICT = [
  { label: 'Male', value: 'Male' },
  { label: 'Female', value: 'Female' },
]

export const NO_INSURANCE = 'none'
export const NO_ACCEPTED_INSURANCE = 'no_accepted_insurance'
export const NO_MEMBER_ID = 'no_member_id'

export const INELIGIBLE_INSURANCE_MESSAGE = {
  none: {
    title: 'Sorry. Insurance coverage is required.',
    body: 'We accept most insurance plans. Visit <a href="https://fireflyhealth.com/care">firefly.health/care</a>to learn more.',
  },
  medicare_medicaid: {
    title: 'Sorry. Firefly isn’t covered by Medicare or Medicaid yet.',
    body: null,
  },
  no_accepted_insurance: {
    title: 'Sorry. Firefly doesn’t accept any other insurance plans now but we’re working on it.',
    body: 'We accept most insurance plans. Visit <a href="https://fireflyhealth.com/care">firefly.health/care</a>to learn more.',
  },
  no_member_id: {
    title: 'Finding your Member ID number',
  },
}

interface OnboardingBffResponse {
  nextScreen: string
}

interface InviteCodeValidationResponse extends OnboardingBffResponse {
  inviteCodeId: number
}

/**
 * Shape of errors rejected by `api.rest.*` calls to BFF onboarding endpoints.
 * Matches the axios `error.response` object surfaced by `api.errorHandler`.
 */
interface OnboardingBffError {
  status: number
  data: {
    errorKey?: string
    detail?: string
    email?: string
    [key: string]: unknown
  }
}

interface SignupTypeOption {
  label: string
  value: SignupType
  subtext: string
}

interface SignupTypeSelectionOptionsResponse {
  options: SignupTypeOption[]
}

const SIGNUP_TYPE_SELECTION_OPTIONS_KEY = ['signupTypeSelectionOptions']

/**
 * Hook to fetch available signup type options from public API.
 * Used to dynamically populate the user type selection screen.
 */
export const useSignupTypeSelectionOptions = () => {
  return useQuery<SignupTypeOption[]>(
    SIGNUP_TYPE_SELECTION_OPTIONS_KEY,
    async () => {
      const { data } = await api.rest.get<SignupTypeSelectionOptionsResponse>(
        '/onboarding/signup-type-options/',
      )
      return data.options
    },
    {
      staleTime: 5 * 60 * 1000, // 5 minutes
      placeholderData: [],
    },
  )
}

/**
 * Hook to call the BFF endpoint for authenticated signup type selection.
 * Used in the authenticated onboarding flow to save signup type and get next screen.
 */
export const useBffWebOnboardingSignupTypeSelection = () => {
  const history = useHistory()
  const setToken = useSetToken()
  const userAuthenticated = useUserAuthenticated()
  const routeToNextScreen = useOnboardingNavigation()
  return useMutation<[SignupType | 'other'], OnboardingBffResponse | undefined>(
    async signupType => {
      try {
        // Bootstrap the lucian User early; bail if the email-verification gate redirected.
        await setToken.handler()
        const user = await userAuthenticated()
        if (!user) return
        const response = await api.rest.post<OnboardingBffResponse>(
          '/bff/web/onboarding/signup-type-selection/',
          { signup_type: signupType },
        )

        routeToNextScreen(response.nextScreen)

        return response
      } catch (error: any) {
        // Handle INELIGIBLE error from BFF
        if (error?.data?.errorKey === 'ineligible') {
          history.push({
            pathname: '/signup/insurance/ineligible',
            state: {
              title: INELIGIBLE_INSURANCE_MESSAGE[NO_INSURANCE]['title'],
              body: INELIGIBLE_INSURANCE_MESSAGE[NO_INSURANCE]['body'],
            },
          })
        } else {
          logger.error('Error submitting signup type:', error)
          history.push({ pathname: '/error' })
        }
        // Re-throw so useMutation can set error state
        throw error
      }
    },
  )
}

/**
 * Hook to call the BFF endpoint for authenticated invite code validation.
 * Used in the authenticated onboarding flow to validate and save invite code.
 */
export const useBffWebOnboardingInviteCodeValidation = () => {
  const setToken = useSetToken()
  const routeToNextScreen = useOnboardingNavigation()
  return useMutation<[string], InviteCodeValidationResponse>(async inviteCode => {
    await setToken.handler()

    const response = await api.rest.post<InviteCodeValidationResponse>(
      '/bff/web/onboarding/invite-code-validation/',
      { invite_code: inviteCode },
    )

    routeToNextScreen(response.nextScreen)

    return response
  })
}

/**
 * Hook to call the BFF endpoint for authenticated insurance payer selection.
 * Used in the authenticated onboarding flow to select and save insurance payer.
 */
export const useBffWebOnboardingInsurancePayerSelection = () => {
  const history = useHistory()
  const setToken = useSetToken()
  const routeToNextScreen = useOnboardingNavigation()
  return useMutation<[number | null], OnboardingBffResponse>(async insurancePayerId => {
    try {
      await setToken.handler()

      const response = await api.rest.post<OnboardingBffResponse>(
        '/bff/web/onboarding/insurance-payer-selection/',
        { insurance_payer_id: insurancePayerId },
      )

      routeToNextScreen(response.nextScreen)

      return response
    } catch (error: any) {
      // Handle INELIGIBLE error from BFF
      if (error?.data?.errorKey === 'ineligible') {
        history.push({
          pathname: '/signup/insurance/ineligible',
          state: {
            title: INELIGIBLE_INSURANCE_MESSAGE[NO_ACCEPTED_INSURANCE]['title'],
            body: INELIGIBLE_INSURANCE_MESSAGE[NO_ACCEPTED_INSURANCE]['body'],
          },
        })
      } else {
        logger.error('Error in insurance payer selection:', error)
        history.push({ pathname: '/error' })
      }
      // Re-throw so useMutation can set error state
      throw error
    }
  })
}

/**
 * Helper function to conditionally include a field in an object
 * @param value - The value to check
 * @param key - The key name for the field
 * @returns An object with the field if value is truthy, otherwise empty object
 */
const includeIf = <K extends string, V>(value: V, key: K): Record<K, V> | {} => {
  return value ? ({ [key]: value } as Record<K, V>) : {}
}

/** Normalises a DOB string to ISO format (yyyy-MM-dd).
 *  Accepts both MM/dd/yyyy (from form input) and already-ISO strings. */
const toISODate = (dob: string): string =>
  /\d{4}-\d{2}-\d{2}/.test(dob) ? dob : format(parse(dob, 'MM/dd/yyyy', new Date()), 'yyyy-MM-dd')

interface InsuranceMemberInfo {
  insurance_payer_id?: number | null
  state?: string | null
}

interface CreateNewAccountPayload {
  signup_type?: string | null
  invite_code?: string | null
  insurance_member_info?: InsuranceMemberInfo
  created_from?: string | null
  first_name?: string | null
  last_name?: string | null
  preferred_name?: string | null
  dob?: string | null
  consent_forms?: number[]
  sex?: string | null
  gender?: string[] | null
  pronouns?: string | null
  preferred_language?: number | null
  preferred_language_other?: string | null
}

// Params for AuthenticatedNameDobCollection screen
export interface NameDobCollectionParams {
  dob: string
  consentForms: number[]

  // Optional fields.
  // The legal-name pair is optional because prospect signup does not collect one — the name a
  // prospect gives is sent as `preferred_name` and their legal name is collected at member
  // conversion. Member callers always supply both.
  firstName?: string
  lastName?: string
  preferredName?: string
  insurancePayerId?: number | null
  inviteCode?: string | null
  signupType?: SignupType
}

/**
 * BFF hook for authenticated web onboarding - create new account
 * Used by Prospect creation flow both Unauthenticated and Authenticated
 * Also used in Authenticated flow by AuthenticatedNameDobCollection screen.
 */
export const useBffWebOnboardingCreateNewAccount = () => {
  const history = useHistory()
  const setToken = useSetToken()
  const auth0 = useAuth0()
  const routeToNextScreen = useOnboardingNavigation()

  return useMutation<[NameDobCollectionParams], OnboardingBffResponse | undefined>(async params => {
    try {
      await setToken.handler()

      const insuranceMemberInfo: InsuranceMemberInfo | undefined =
        params.insurancePayerId != null
          ? { insurance_payer_id: params.insurancePayerId }
          : undefined

      const payload: CreateNewAccountPayload = {
        // Omitted rather than sent blank when absent: a null pair would overwrite a legal name an
        // earlier signup attempt had already persisted.
        ...includeIf(params.firstName, 'first_name'),
        ...includeIf(params.lastName, 'last_name'),
        ...includeIf(params.preferredName, 'preferred_name'),
        dob: toISODate(params.dob),
        consent_forms: params.consentForms,
        created_from: 'web',
        // Conditional fields
        ...includeIf(params.inviteCode, 'invite_code'),
        ...includeIf(insuranceMemberInfo, 'insurance_member_info'),
        ...includeIf(params.signupType, 'signup_type'),
      }

      const response = await api.rest.post<OnboardingBffResponse>(
        '/bff/web/onboarding/create-new-account/',
        payload,
      )

      // Account created — drop the resumable signup draft so a reload/re-entry
      // through Landing doesn't route back.
      clearSignupDraft(auth0.user?.email ?? '')

      routeToNextScreen(response.nextScreen)

      return response
    } catch (error: any) {
      logger.error('Error in create new account:', error)
      history.push({ pathname: '/error' })
      throw error
    }
  })
}

export interface DemographicsCollectionParams {
  state: StateItemsProps
  sex: string
  preferredLanguage: number | null

  // Optional fields
  gender?: string[] | null
  pronouns?: string | null
  preferredLanguageOther?: string | null
}

/**
 * BFF hook for authenticated web onboarding - update new account
 * Used in Authenticated flow by AuthenticatedDemographicsCollection screen.
 */
export const useBffWebOnboardingDemographicsCollection = () => {
  const history = useHistory()
  const setToken = useSetToken()
  const routeToNextScreen = useOnboardingNavigation()

  return useMutation<[DemographicsCollectionParams], OnboardingBffResponse | void>(async params => {
    try {
      await setToken.handler()

      const createdFrom = 'web'

      const insuranceMemberInfo: InsuranceMemberInfo = {
        state: params.state.value,
      }

      const payload: CreateNewAccountPayload = {
        insurance_member_info: insuranceMemberInfo,
        sex: params.sex,
        preferred_language: params.preferredLanguage,
        // Conditional fields
        ...includeIf(params.gender, 'gender'),
        ...includeIf(params.pronouns, 'pronouns'),
        ...includeIf(params.preferredLanguageOther, 'preferred_language_other'),
        // Common fields
        ...includeIf(createdFrom, 'created_from'),
      }

      const response = await api.rest.post<OnboardingBffResponse>(
        '/bff/web/onboarding/create-new-account/',
        payload,
      )

      routeToNextScreen(response.nextScreen)

      return response
    } catch (error: any) {
      // Handle INELIGIBLE_STATE error from BFF
      if (error?.data?.errorKey === 'ineligible_state') {
        history.push({ pathname: '/signup/cannot-service', state: { states: params.state } })
      } else {
        logger.error('Error in create new account:', error)
        history.push({ pathname: '/error' })
      }

      throw error
    }
  })
}

export type NameAndDemographicCollectionParams = NameDobCollectionParams &
  DemographicsCollectionParams

/**
 * BFF hook for unauthenticated web onboarding
 * Used in UnAuthenticated flow after DemographicCollectionForm screen.
 */

export const useBffWebOnboardingCreateMemberAccount = () => {
  const history = useHistory()
  const setToken = useSetToken()
  const auth0 = useAuth0()
  const routeToNextScreen = useOnboardingNavigation()

  return useMutation<[NameAndDemographicCollectionParams], OnboardingBffResponse | void>(
    async params => {
      try {
        await setToken.handler()

        const insuranceMemberInfo: InsuranceMemberInfo = {
          state: params.state.value,
          ...includeIf(params.insurancePayerId, 'insurance_payer_id'),
        }

        const payload: CreateNewAccountPayload = {
          first_name: params.firstName,
          last_name: params.lastName,
          dob: toISODate(params.dob),
          consent_forms: params.consentForms,
          insurance_member_info: insuranceMemberInfo,
          sex: params.sex,
          preferred_language: params.preferredLanguage,
          created_from: 'web',
          ...includeIf(params.preferredName, 'preferred_name'),
          ...includeIf(params.gender, 'gender'),
          ...includeIf(params.pronouns, 'pronouns'),
          ...includeIf(params.preferredLanguageOther, 'preferred_language_other'),
          ...includeIf(params.inviteCode, 'invite_code'),
          ...includeIf(params.signupType, 'signup_type'),
        }

        const response = await api.rest.post<OnboardingBffResponse>(
          '/bff/web/onboarding/create-new-account/',
          payload,
        )

        // Account created — drop the resumable signup draft so a reload/re-entry
        // through Landing doesn't route back.
        clearSignupDraft(auth0.user?.email ?? '')

        routeToNextScreen(response.nextScreen)

        return response
      } catch (error: any) {
        // Handle INELIGIBLE_STATE error from BFF
        if (error?.data?.errorKey === 'ineligible_state') {
          history.push({ pathname: '/signup/cannot-service', state: { states: params.state } })
        } else {
          logger.error('Error in create new account:', error)
          history.push({ pathname: '/error' })
        }

        throw error
      }
    },
  )
}

interface MemberIdCollectionLoaderResponse {
  signupType: SignupType | null
}

const MEMBER_ID_COLLECTION_LOADER_KEY = ['bffWebOnboardingMemberIdCollection']

/**
 * Hook to fetch loader data for the authenticated member id collection screen.
 * Returns signup_type from the user's onboarding_state.
 */
export const useBffWebOnboardingMemberIdCollectionLoader = () => {
  const history = useHistory()
  const setToken = useSetToken()
  return useQuery<MemberIdCollectionLoaderResponse>(
    MEMBER_ID_COLLECTION_LOADER_KEY,
    async () => {
      await setToken.handler()
      return (
        await api.rest.get<MemberIdCollectionLoaderResponse>(
          '/bff/web/onboarding/member-id-collection/',
        )
      ).data
    },
    {
      onError: error => {
        logger.error('Error loading member id collection:', error)
        history.push({ pathname: '/error' })
      },
    },
  )
}

/**
 * BFF hook for authenticated web onboarding - member id collection
 * Used in Authenticated flow by AuthenticatedMemberIdCollection screen.
 */
export const useBffWebOnboardingMemberIdCollection = () => {
  const history = useHistory()
  const setToken = useSetToken()
  const auth0 = useAuth0()
  const routeToNextScreen = useOnboardingNavigation()

  return useMutation<[string], OnboardingBffResponse | void, OnboardingBffError>(async memberId => {
    try {
      await setToken.handler()

      const response = await api.rest.post<OnboardingBffResponse>(
        '/bff/web/onboarding/member-id-collection/',
        { member_id: memberId },
      )

      routeToNextScreen(response.nextScreen)

      return response
    } catch (err) {
      const error = err as OnboardingBffError
      // Handle DUPLICATE_ACCOUNT error from BFF (HTTP 409)
      if (error?.data?.errorKey === 'duplicate_account') {
        secureCache.clear()
        storedToken.remove()
        // The masked_email will be cleared in existing screen useEffect.
        // This value is needed to show the email in existing account screen.
        secureCache.set('masked_email', error.data.email ?? '')
        auth0.logout({ returnTo: window.location.origin + '/signup/existing' })
        // Return without throwing - logout will redirect the user
        return
      } else if (error?.status !== 400) {
        logger.error('Error in member id collection:', error)
        history.push({ pathname: '/error' })
      }
      // Re-throw so useMutation can set error state for 400 errors
      throw error
    }
  })
}
